import { toLocalYMD } from "../utils/localDate";
// src/service/sharedExpenseStore.js

/**
 * Shared Expense Cache & Event Store
 * 
 * Provides instantaneous page rendering for SharedExpense by caching:
 * - Shared expenses list
 * - Fetched categories
 * - Shared partner / group details
 * 
 * Eliminates redundant blocking network calls when reopening the page.
 * Refreshes in background or immediately when an update (add/edit/delete) is dispatched.
 */

const CACHE_KEY_PREFIX = "shared_expense_cache_";
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes fresh window

export const getSharedExpenseCache = (userEmail) => {
  if (!userEmail) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${userEmail.toLowerCase()}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed;
  } catch (err) {
    console.warn("Failed to read shared expense cache:", err);
    return null;
  }
};

export const setSharedExpenseCache = (userEmail, data) => {
  if (!userEmail) return;
  try {
    const rawExpenses = data.expenses || [];

    // Calculate all-time total from the complete dataset before filtering
    const allTimeTotal = rawExpenses.reduce(
      (sum, e) => sum + Number(e.amount || 0),
      0
    );

    // Keep only the last 1 year (365 days) of records in local cache
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const oneYearThreshold = toLocalYMD(oneYearAgo);

    const oneYearExpenses = rawExpenses.filter((e) => {
      if (!e.date) return true; // keep undated items just in case
      return e.date >= oneYearThreshold;
    });

    const payload = {
      timestamp: Date.now(),
      expenses: oneYearExpenses,
      allTimeTotal: allTimeTotal,
      categories: data.categories || [],
      sharedTarget: data.sharedTarget || "",
      sharedWithName: data.sharedWithName || "",
      groupName: data.groupName || "",
    };
    localStorage.setItem(`${CACHE_KEY_PREFIX}${userEmail.toLowerCase()}`, JSON.stringify(payload));
  } catch (err) {
    console.warn("Failed to write shared expense cache:", err);
  }
};

export const clearSharedExpenseCache = (userEmail) => {
  try {
    if (userEmail) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${userEmail.toLowerCase()}`);
    } else {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(k);
        }
      });
    }
  } catch (err) {
    console.warn("Failed to clear shared expense cache:", err);
  }
};

export const isCacheFresh = (cacheData) => {
  if (!cacheData || !cacheData.timestamp) return false;
  return Date.now() - cacheData.timestamp < CACHE_EXPIRY_MS;
};

// Custom event dispatcher to broadcast shared expense updates across components
export const dispatchSharedExpenseUpdated = () => {
  window.dispatchEvent(new CustomEvent("shared-expense-updated"));
};

// Dispatch a manual refresh request from header button
export const dispatchManualRefresh = (pathname = "") => {
  window.dispatchEvent(new CustomEvent("app-manual-refresh", { detail: { pathname } }));
};

